<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { Icon } from '@iconify/vue'
import { copyToClipboard } from '@/utils'
import { useChatmixCallsStore } from '@/stores/chatmixCalls'
import { useDraggable } from '@/composables/useDraggable'

const store = useChatmixCallsStore()

const { pos, isDragging, startDrag } = useDraggable()

const rootEl = ref(null)
const localVideoEl = ref(null)
const remoteVideoEl = ref(null)
const canvasEl = ref(null)
const copied = ref(false)
const isMaximized = ref(false)
const hasDragged = ref(false)

const annotating = ref(false)
const currentColor = ref('#ef4444')
const ANNOTATION_COLORS = ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#ffffff']

let activeStroke = null
let lastSentPoint = null
let strokes = []

const visible = computed(() => store.hasActiveCall || (store.status === 'ended' && store.recordingUrl))

const headerLabel = computed(() => {
    if (store.reconnecting) return 'Reconectando…'
    if (store.isCreating) return 'Iniciando chamada…'
    if (store.isConnecting) return 'Conectando…'
    if (store.isInCall) return store.clientJoined ? 'Em chamada' : 'Aguardando cliente'
    if (store.isWaiting) return store.error ? 'Não foi possível entrar' : 'Aguardando cliente'
    return 'Chamada'
})

const durationLabel = computed(() => {
    if (store.duration == null) return '00:00'
    const total = Math.max(0, Math.floor(Number(store.duration)))
    const m = Math.floor(total / 60).toString().padStart(2, '0')
    const s = (total % 60).toString().padStart(2, '0')
    return `${m}:${s}`
})

const containerStyle = computed(() => {
    if (isMaximized.value) {
        return {
            top: '24px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 'min(1100px, 96vw)',
            height: 'calc(100vh - 48px)',
        }
    }

    const base = {
        width: '360px',
        maxWidth: '95vw',
        maxHeight: '90vh',
    }

    if (!hasDragged.value) {
        return { ...base, bottom: '24px', right: '24px' }
    }

    return {
        ...base,
        top: `${pos.top}px`,
        left: `${pos.left}px`,
    }
})

// Os elementos <video> só existem no estado "active". Como a conexão LiveKit
// acontece antes (estado "connecting"), anexamos as tracks depois que eles
// forem renderizados, reagindo à mudança de status e à chegada de tracks.
watch([() => store.isInCall, () => store.remoteTracks.length, () => store.remoteScreenSharing], () => {
    if (!store.isInCall) return
    nextTick(() => {
        store.attachMedia(localVideoEl.value, remoteVideoEl.value)
        syncCanvasSize()
    })
})

watch(isMaximized, () => {
    if (!store.isInCall) return
    nextTick(syncCanvasSize)
})

watch(annotating, () => {
    if (!store.isInCall) return
    nextTick(syncCanvasSize)
})

watch(() => store.isInCall, (active) => {
    if (active) return
    annotating.value = false
    activeStroke = null
    lastSentPoint = null
    strokes = []
})

watch(() => store.clientJoined, (joined) => {
    if (joined) return
    annotating.value = false
    activeStroke = null
    lastSentPoint = null
})

function syncCanvasSize() {
    const canvas = canvasEl.value
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const width = Math.max(1, Math.round(rect.width))
    const height = Math.max(1, Math.round(rect.height))
    if (canvas.width === width && canvas.height === height) return
    canvas.width = width
    canvas.height = height
    redraw()
}

function pointFromEvent(e) {
    const rect = canvasEl.value.getBoundingClientRect()
    const x = (e.clientX - rect.left) / rect.width
    const y = (e.clientY - rect.top) / rect.height
    return {
        x: Math.min(1, Math.max(0, x)),
        y: Math.min(1, Math.max(0, y)),
    }
}

function distance(a, b) {
    return Math.hypot(a.x - b.x, a.y - b.y)
}

function drawStroke(ctx, canvas, stroke) {
    if (stroke.points.length < 2) return
    ctx.strokeStyle = stroke.color
    ctx.lineWidth = 3
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.beginPath()
    ctx.moveTo(stroke.points[0].x * canvas.width, stroke.points[0].y * canvas.height)
    for (let i = 1; i < stroke.points.length; i++) {
        ctx.lineTo(stroke.points[i].x * canvas.width, stroke.points[i].y * canvas.height)
    }
    ctx.stroke()
}

function redraw() {
    const canvas = canvasEl.value
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    for (const stroke of strokes) drawStroke(ctx, canvas, stroke)
    if (activeStroke) drawStroke(ctx, canvas, activeStroke)
}

function onPointerDown(e) {
    if (!annotating.value) return
    e.preventDefault()
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
    const p = pointFromEvent(e)
    activeStroke = { id, color: currentColor.value, points: [p] }
    lastSentPoint = p
    store.sendAnnotation({ type: 'stroke-start', id, color: currentColor.value, x: p.x, y: p.y })
    redraw()
    canvasEl.value.setPointerCapture(e.pointerId)
}

function onPointerMove(e) {
    if (!activeStroke) return
    const p = pointFromEvent(e)
    activeStroke.points.push(p)
    if (!lastSentPoint || distance(p, lastSentPoint) > 0.004) {
        lastSentPoint = p
        store.sendAnnotation({ type: 'stroke-point', id: activeStroke.id, x: p.x, y: p.y })
    }
    redraw()
}

function onPointerUp() {
    if (!activeStroke) return
    const last = activeStroke.points[activeStroke.points.length - 1]
    store.sendAnnotation({ type: 'stroke-end', id: activeStroke.id, x: last.x, y: last.y })
    strokes.push(activeStroke)
    activeStroke = null
    lastSentPoint = null
}

function undoLastStroke() {
    if (activeStroke) return
    if (!strokes.length) return
    strokes.pop()
    redraw()
    store.sendAnnotation({ type: 'undo' })
}

function clearStrokes() {
    activeStroke = null
    lastSentPoint = null
    strokes = []
    redraw()
    store.sendAnnotation({ type: 'clear' })
}

function toggleAnnotating() {
    if (!store.clientJoined) return
    annotating.value = !annotating.value
    if (!annotating.value && activeStroke) onPointerUp()
}

function onDragStart(event) {
    if (isMaximized.value) return

    if (!hasDragged.value && rootEl.value) {
        const rect = rootEl.value.getBoundingClientRect()
        pos.top = rect.top
        pos.left = rect.left
        hasDragged.value = true
    }

    startDrag(event)
}

function toggleMaximize() {
    isMaximized.value = !isMaximized.value
}

function joinFromWaiting() {
    store.joinCall()
}

function copyLink() {
    if (!store.joinUrl) return
    copyToClipboard(store.joinUrl, true)
    copied.value = true
    setTimeout(() => { copied.value = false }, 1500)
}

function close() {
    isMaximized.value = false
    if (store.hasActiveCall) store.endCall()
    else store.reset()
}
</script>

<template>
    <Transition name="chatmix-call">
        <div
            v-if="visible"
            ref="rootEl"
            class="fixed z-50"
            :class="{ 'select-none': isDragging }"
            :style="containerStyle"
        >
            <div
                class="h-full flex flex-col rounded-2xl shadow-2xl border overflow-hidden bg-white/95 dark:bg-neutral-800/95 border-neutral-200 dark:border-neutral-600"
            >
                <!-- Cabeçalho (arrastável) -->
                <div
                    class="shrink-0 px-4 py-2 flex items-center gap-2 text-white text-xs font-medium bg-chat-primary cursor-move"
                    @mousedown="onDragStart"
                    @touchstart="onDragStart"
                >
                    <Icon icon="mdi:video" class="w-4 h-4" />
                    <span>{{ headerLabel }}</span>
                    <span v-if="store.isInCall" class="font-mono">{{ durationLabel }}</span>

                    <div class="ml-auto flex items-center gap-1">
                        <button
                            data-no-drag
                            class="flex items-center justify-center w-6 h-6 rounded hover:bg-white/20"
                            :title="isMaximized ? 'Restaurar' : 'Maximizar'"
                            @click="toggleMaximize"
                        >
                            <Icon :icon="isMaximized ? 'mdi:fullscreen-exit' : 'mdi:fullscreen'" class="w-4 h-4" />
                        </button>
                        <button
                            data-no-drag
                            class="flex items-center justify-center w-6 h-6 rounded hover:bg-white/20"
                            title="Fechar"
                            @click="close"
                        >
                            <Icon icon="mdi:close" class="w-4 h-4" />
                        </button>
                    </div>
                </div>

                <!-- Conteúdo -->
                <div class="flex-1 min-h-0 flex flex-col overflow-hidden">
                    <!-- Criando / Conectando -->
                    <div
                        v-if="store.isCreating || store.isConnecting"
                        class="flex-1 px-4 py-10 flex flex-col items-center justify-center gap-3 text-sm text-slate-500 dark:text-neutral-300"
                    >
                        <Icon icon="mdi:loading" class="w-8 h-8 animate-spin text-chat-primary" />
                        {{ store.isCreating ? 'Criando chamada…' : 'Conectando ao LiveKit…' }}
                    </div>

                    <!-- Aguardando cliente -->
                    <div v-else-if="store.isWaiting" class="px-4 py-4 flex flex-col gap-3">
                        <p
                            v-if="store.error"
                            class="flex items-start gap-2 text-xs px-3 py-2 rounded-md bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300"
                        >
                            <Icon icon="mdi:alert-circle-outline" class="shrink-0 w-4 h-4 mt-px" />
                            {{ store.error }}
                        </p>
                        <p class="text-sm text-slate-600 dark:text-neutral-300">
                            Link da chamada (o cliente também o recebe pelo WhatsApp):
                        </p>
                        <div class="flex items-center gap-2">
                            <input
                                :value="store.joinUrl"
                                readonly
                                class="w-full text-xs px-3 py-2 rounded-md border border-neutral-200 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-700 dark:text-neutral-200"
                            >
                            <button
                                data-no-drag
                                class="shrink-0 p-2 rounded-md bg-neutral-100 dark:bg-neutral-700 hover:bg-neutral-200 dark:hover:bg-neutral-600"
                                @click="copyLink"
                            >
                                <Icon :icon="copied ? 'mdi:check' : 'mdi:content-copy'" class="w-4 h-4" />
                            </button>
                        </div>
                        <div class="flex gap-2">
                            <button
                                data-no-drag
                                class="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-chat-primary text-white text-sm font-medium hover:opacity-90"
                                @click="joinFromWaiting"
                            >
                                <Icon icon="mdi:phone-in-talk" class="w-4 h-4" />
                                Entrar na chamada
                            </button>
                            <button
                                data-no-drag
                                class="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-neutral-300 dark:border-neutral-600 text-sm font-medium text-slate-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                                @click="store.endCall()"
                            >
                                <Icon icon="mdi:phone-hangup" class="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    <!-- Em chamada -->
                    <div v-else-if="store.isInCall" class="relative bg-black flex-1 min-h-0 flex flex-col">
                        <div v-if="!store.clientJoined" class="shrink-0 flex items-center gap-2 px-3 py-2 bg-neutral-900">
                            <span class="shrink-0 text-[11px] text-neutral-300">Link do cliente</span>
                            <input
                                :value="store.joinUrl"
                                readonly
                                class="flex-1 min-w-0 text-[11px] px-2 py-1 rounded border border-neutral-700 bg-neutral-800 text-neutral-200"
                            >
                            <button
                                data-no-drag
                                class="shrink-0 p-1.5 rounded bg-neutral-800 hover:bg-neutral-700 text-neutral-200"
                                @click="copyLink"
                            >
                                <Icon :icon="copied ? 'mdi:check' : 'mdi:content-copy'" class="w-4 h-4" />
                            </button>
                        </div>
                        <div
                            class="relative w-full bg-black"
                            :class="isMaximized || store.remoteScreenSharing || annotating ? 'flex-1 min-h-[14rem]' : 'h-56'"
                        >
                            <video
                                ref="remoteVideoEl"
                                autoplay
                                playsinline
                                class="w-full h-full bg-black object-contain"
                                @loadedmetadata="syncCanvasSize"
                                @resize="syncCanvasSize"
                            />
                            <canvas
                                ref="canvasEl"
                                data-no-drag
                                class="annotation-canvas"
                                :class="{ drawing: annotating }"
                                @pointerdown="onPointerDown"
                                @pointermove="onPointerMove"
                                @pointerup="onPointerUp"
                                @pointercancel="onPointerUp"
                            />
                        </div>
                        <video
                            v-show="!annotating"
                            ref="localVideoEl"
                            autoplay
                            playsinline
                            muted
                            class="absolute right-2 z-10 w-24 h-32 object-cover rounded-lg border border-white/40 bg-neutral-800"
                            :class="isMaximized ? 'bottom-16' : 'bottom-14'"
                        />
                        <div class="shrink-0 flex items-center justify-center gap-2 py-3 bg-black">
                            <button
                                data-no-drag
                                class="flex items-center justify-center w-10 h-10 rounded-full text-white"
                                :class="store.micMuted ? 'bg-red-500' : 'bg-white/20 hover:bg-white/30'"
                                :title="store.micMuted ? 'Ativar microfone' : 'Silenciar microfone'"
                                @click="store.toggleMute()"
                            >
                                <Icon :icon="store.micMuted ? 'mdi:microphone-off' : 'mdi:microphone'" class="w-5 h-5" />
                            </button>
                            <button
                                data-no-drag
                                class="flex items-center justify-center w-10 h-10 rounded-full text-white"
                                :class="store.screenSharing ? 'bg-chat-primary' : 'bg-white/20 hover:bg-white/30'"
                                :title="store.screenSharing ? 'Parar de compartilhar tela' : 'Compartilhar tela'"
                                @click="store.toggleScreenShare()"
                            >
                                <Icon :icon="store.screenSharing ? 'mdi:monitor-off' : 'mdi:monitor-share'" class="w-5 h-5" />
                            </button>
                            <button
                                data-no-drag
                                class="flex items-center justify-center w-10 h-10 rounded-full text-white"
                                :class="[
                                    annotating ? 'bg-chat-primary' : 'bg-white/20 hover:bg-white/30',
                                    !store.clientJoined ? 'opacity-40 cursor-not-allowed' : '',
                                ]"
                                :disabled="!store.clientJoined"
                                :title="store.clientJoined ? 'Anotar na tela do cliente' : 'Aguarde o cliente entrar na chamada'"
                                @click="toggleAnnotating"
                            >
                                <Icon icon="mdi:pencil" class="w-5 h-5" />
                            </button>
                            <button
                                data-no-drag
                                class="flex items-center justify-center w-12 h-12 rounded-full bg-red-500 text-white hover:bg-red-600"
                                @click="store.endCall()"
                            >
                                <Icon icon="mdi:phone-hangup" class="w-6 h-6" />
                            </button>
                        </div>
                        <div
                            v-if="annotating"
                            class="shrink-0 flex items-center justify-center gap-2 px-3 pb-3 bg-black"
                        >
                            <button
                                v-for="color in ANNOTATION_COLORS"
                                :key="color"
                                data-no-drag
                                class="w-5 h-5 rounded-full border-2"
                                :class="currentColor === color ? 'border-white scale-110' : 'border-transparent'"
                                :style="{ backgroundColor: color }"
                                @click="currentColor = color"
                            />
                            <button
                                data-no-drag
                                class="ml-1 flex items-center justify-center w-8 h-8 rounded-full text-white bg-white/20 hover:bg-white/30"
                                title="Desfazer"
                                @click="undoLastStroke"
                            >
                                <Icon icon="mdi:undo" class="w-4 h-4" />
                            </button>
                            <button
                                data-no-drag
                                class="flex items-center justify-center w-8 h-8 rounded-full text-white bg-white/20 hover:bg-white/30"
                                title="Limpar"
                                @click="clearStrokes"
                            >
                                <Icon icon="mdi:eraser" class="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    <!-- Encerrada -->
                    <div v-else-if="store.status === 'ended'" class="px-4 py-4 flex flex-col gap-3">
                        <p class="text-sm text-slate-600 dark:text-neutral-300">Chamada encerrada.</p>
                        <a
                            v-if="store.recordingUrl"
                            :href="store.recordingUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-chat-primary text-white text-sm font-medium hover:opacity-90"
                        >
                            <Icon icon="mdi:download" class="w-4 h-4" />
                            Baixar gravação
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </Transition>
</template>

<style scoped>
.chatmix-call-enter-active {
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.chatmix-call-leave-active {
    transition: all 0.25s ease-in;
}
.chatmix-call-enter-from {
    opacity: 0;
    transform: translateY(40px) scale(0.9);
}
.chatmix-call-leave-to {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
}
.annotation-canvas {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    touch-action: none;
    z-index: 20;
}
.annotation-canvas.drawing {
    cursor: crosshair;
    pointer-events: auto;
}
.annotation-canvas:not(.drawing) {
    pointer-events: none;
}
</style>
